/**
 * ChatInput — Textarea + Send + (Phase 5) Attach (#1180).
 */
import { useEffect, useRef, useState } from 'react';
import { Send, Square, Paperclip, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function ChatInput({ onSend, onStop, streaming, disabled }) {
  const { t } = useLanguage();
  const [value, setValue] = useState('');
  const [files, setFiles] = useState([]);
  const taRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 220) + 'px';
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || streaming) return;
    onSend?.(trimmed, files);
    setValue('');
    setFiles([]);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onPickFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;
    const remaining = 5 - files.length;
    const accepted = [];
    for (const f of incoming.slice(0, remaining)) {
      if (f.size > 10 * 1024 * 1024) continue;
      accepted.push(f);
    }
    setFiles([...files, ...accepted]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  return (
    <div className="border-t border-line bg-surface-card px-4 py-3">
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-[8px] border border-line bg-surface-input text-xs text-[var(--text-secondary)]">
              <Paperclip size={11} />
              <span className="truncate max-w-[180px]">{f.name}</span>
              <button onClick={() => removeFile(i)} className="text-[var(--text-tertiary)] hover:text-red-500">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 border border-line rounded-[14px] bg-surface-input focus-within:border-[#338edc] transition-colors px-3 py-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || files.length >= 5}
          title={t('chat.attach') || 'Anhängen'}
          className="p-1.5 rounded-[8px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent transition-colors flex-shrink-0"
        >
          <Paperclip size={16} />
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp"
          onChange={onPickFiles}
          className="hidden"
        />
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={t('chat.placeholder') || 'Nachricht an Claude…'}
          disabled={disabled}
          className="flex-1 resize-none bg-transparent text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none leading-relaxed py-1.5 max-h-[220px] min-h-[24px]"
        />
        {streaming ? (
          <button
            type="button"
            onClick={onStop}
            title={t('common.stop') || 'Stop'}
            className="flex-shrink-0 w-8 h-8 rounded-[10px] bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
          >
            <Square size={14} fill="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!value.trim() || disabled}
            title={t('chat.send') || 'Senden'}
            className="flex-shrink-0 w-8 h-8 rounded-[10px] bg-[#338edc] hover:bg-[#2a7bc4] text-white flex items-center justify-center transition-colors disabled:opacity-40 disabled:hover:bg-[#338edc]"
          >
            <Send size={14} />
          </button>
        )}
      </div>
      <div className="text-[11px] text-[var(--text-tertiary)] mt-1.5 px-1">
        {t('chat.attach_max') || 'Max 5 Dateien · 10 MB · PDF/CSV/TXT/PNG/JPG'}
      </div>
    </div>
  );
}
